/**
 * Cart Drawer Component - Shopify 2.0
 *
 * Implementa cart drawer seguindo as práticas oficiais da Shopify:
 * - Web Components para encapsulamento
 * - Fetch API para Cart API
 * - Acessibilidade (ARIA, keyboard)
 * - Performance otimizada
 * - Free shipping bar: visível apenas quando país = US (detecção por IP no client)
 *
 * Referências:
 * - https://shopify.dev/docs/api/ajax/reference/cart
 * - https://shopify.dev/docs/storefronts/themes/best-practices
 */

const FREE_SHIPPING_STORAGE_KEY = "afit_free_shipping_country";

function getCartApi() {
  if (window.AfitCartApi) return window.AfitCartApi;

  const ROOT_URL = window.Shopify?.routes?.root || "/";
  const state = {
    cache: null,
    inflight: null,
    lastFetchAt: 0,
  };
  const MIN_INTERVAL = 800;

  function fetchCart(options = {}) {
    const force = options.force === true;
    const now = Date.now();

    if (!force && state.cache && now - state.lastFetchAt < MIN_INTERVAL) {
      return Promise.resolve(state.cache);
    }

    if (state.inflight) return state.inflight;

    state.inflight = fetch(`${ROOT_URL}cart.js`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error("Cart fetch failed");
        return response.json();
      })
      .then((cart) => {
        state.cache = cart;
        state.lastFetchAt = Date.now();
        return cart;
      })
      .finally(() => {
        state.inflight = null;
      });

    return state.inflight;
  }

  function invalidate() {
    state.cache = null;
    state.lastFetchAt = 0;
  }

  window.AfitCartApi = { fetchCart, invalidate };
  return window.AfitCartApi;
}

/**
 * Free shipping is now offered site-wide, so the bar is rendered visible by
 * Liquid for every visitor. This function is kept as a no-op to preserve the
 * call site without re-introducing the legacy US-only IP detection.
 */
function detectCountryAndToggleFreeShippingBar() {}

if (!customElements.get("cart-drawer")) {
  customElements.define(
    "cart-drawer",
    class CartDrawer extends HTMLElement {
      constructor() {
        super();
        this.drawer = this.querySelector("#CartDrawer");
        this.closeButtons = this.querySelectorAll("[data-close-drawer]");
        this.isOpen = false;

        this.addEventListener("keyup", (evt) => evt.code === "Escape" && this.close());
        this.closeButtons.forEach((button) => {
          button.addEventListener("click", this.close.bind(this));
        });

        // Setup loyalty button
        this.setupLoyaltyButton();
      }

      setupLoyaltyButton() {
        // Delegated so both the header button and the empty-state banner
        // button (re-rendered with the items snippet) trigger BON Loyalty.
        this.addEventListener("click", (event) => {
          const btn = event.target.closest("[data-open-loyalty]");
          if (!btn || !this.contains(btn)) return;

          const originalBtn =
            document.getElementById("bon-loyalty-btn") ||
            document.querySelector('[id*="bon-loyalty"]') ||
            document.querySelector('button[aria-label="BON-Loyalty-btn"]');

          if (originalBtn) {
            originalBtn.click();
          }
        });
      }

      open() {
        this.isOpen = true;
        this.classList.add("is-open");
        this.drawer.setAttribute("aria-hidden", "false");

        document.body.classList.add("cart-drawer-open");

        this.drawer.focus();
        this.dispatchEvent(new CustomEvent("cart-drawer:open", { bubbles: true }));
      }

      close() {
        this.isOpen = false;
        this.classList.remove("is-open");
        this.drawer.setAttribute("aria-hidden", "true");

        document.body.classList.remove("cart-drawer-open");

        this.dispatchEvent(new CustomEvent("cart-drawer:close", { bubbles: true }));
      }
    }
  );
}

if (!customElements.get("cart-remove-button")) {
  customElements.define(
    "cart-remove-button",
    class CartRemoveButton extends HTMLElement {
      constructor() {
        super();

        this.addEventListener("click", (event) => {
          event.preventDefault();
          if (this.dataset.processing === "true") return;
          const line = parseInt(this.dataset.line);
          this.updateCart(line, 0);
        });
      }

      async updateCart(line, quantity) {
        const cartItem = this.closest(".cart-item");
        const button = this.querySelector("button");
        const checkoutButton = document.querySelector('#CartDrawer-Form button[name="checkout"]');

        this.dataset.processing = "true";
        this.classList.add("loading");
        button?.setAttribute("disabled", "");
        checkoutButton?.setAttribute("disabled", "");
        cartItem?.setAttribute("aria-busy", "true");

        if (cartItem) {
          cartItem.style.height = `${cartItem.offsetHeight}px`;
          requestAnimationFrame(() => cartItem.classList.add("is-removing"));
        }

        const sectionId = document.getElementById("CartDrawer-Items")?.dataset.id || "cart-drawer";

        const body = JSON.stringify({
          line: line,
          quantity: quantity,
          sections: sectionId,
          sections_url: window.location.pathname,
        });

        let completed = false;

        try {
          const response = await fetch("/cart/change.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Accept: "application/json",
            },
            credentials: "same-origin",
            body: body,
          });
          const state = await response.json();

          if (!response.ok || state.errors || state.status === "bad_request") {
            throw new Error(state.description || state.message || "Cart update failed");
          }

          getCartApi().invalidate();
          this.renderContents(state);
          completed = true;
        } catch (error) {
          console.error("Error removing cart item:", error);
          window.CartNotification?.error({
            title: "Cart not updated",
            message: "Please try removing the item again.",
            type: "error",
          });
        } finally {
          if (!completed && cartItem) {
            cartItem.classList.remove("is-removing");
            cartItem.removeAttribute("aria-busy");
            requestAnimationFrame(() => {
              cartItem.style.height = "";
            });
          }

          delete this.dataset.processing;
          this.classList.remove("loading");
          button?.removeAttribute("disabled");
          document.querySelector('#CartDrawer-Form button[name="checkout"]')?.removeAttribute("disabled");
        }
      }

      renderContents(parsedState) {
        const sectionId = getCartDrawerSectionId();
        renderCartDrawerSection(parsedState.sections?.[sectionId]);
        updateCartUi(parsedState);
      }

      getSectionInnerHTML(html, selector) {
        return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
      }

      updateCartCount(count) {
        const cartCountElements = document.querySelectorAll(".cart-count, [data-cart-count]");
        cartCountElements.forEach((element) => {
          element.textContent = count;
          element.classList.toggle("hidden", count === 0);
        });
      }

      formatMoney(cents) {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(cents / 100);
      }
    }
  );
}

if (!customElements.get("quantity-input")) {
  customElements.define(
    "quantity-input",
    class QuantityInput extends HTMLElement {
      constructor() {
        super();
        this.input = this.querySelector("input");
        this.changeEvent = new Event("change", { bubbles: true });

        this.querySelectorAll("button").forEach((button) => button.addEventListener("click", this.onButtonClick.bind(this)));

        this.input.addEventListener("change", this.onInputChange.bind(this));
      }

      onButtonClick(event) {
        event.preventDefault();
        const button = event.currentTarget;
        const line = parseInt(button.dataset.line);
        const newQuantity = parseInt(button.dataset.quantity);

        if (newQuantity < 1) return;

        this.input.value = newQuantity;
        this.updateCart(line, newQuantity);
      }

      onInputChange(event) {
        const value = parseInt(event.target.value);
        if (value < 1) {
          event.target.value = 1;
          return;
        }

        const line = parseInt(event.target.dataset.line);
        this.updateCart(line, value);
      }

      updateCart(line, quantity) {
        this.classList.add("loading");

        const sectionId = document.getElementById("CartDrawer-Items")?.dataset.id || "cart-drawer";

        const body = JSON.stringify({
          line: line,
          quantity: quantity,
          sections: sectionId,
          sections_url: window.location.pathname,
        });

        fetch("/cart/change.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: body,
        })
          .then((response) => response.json())
          .then((state) => {
            if (state.errors || state.status === "bad_request") {
              console.error(state);
              this.input.value = this.input.defaultValue;
              this.classList.remove("loading");
              return;
            }

            this.renderContents(state);
            this.classList.remove("loading");
          })
          .catch((error) => {
            console.error("Error:", error);
            this.input.value = this.input.defaultValue;
            this.classList.remove("loading");
          });
      }

      renderContents(parsedState) {
        const cartDrawerItems = document.getElementById("CartDrawer-Items");
        const sectionId = cartDrawerItems?.dataset.id || "cart-drawer";

        if (cartDrawerItems && parsedState.sections && parsedState.sections[sectionId]) {
          const parser = new DOMParser();
          const doc = parser.parseFromString(parsedState.sections[sectionId], "text/html");

          // Update items list
          const newItems = doc.querySelector("#CartDrawer-Items");
          if (newItems) {
            cartDrawerItems.innerHTML = newItems.innerHTML;
          }

          // Update recommendations if visible
          const cartDrawerRecommendations = document.getElementById("CartDrawer-Recommendations");
          const newRecommendations = doc.querySelector("#CartDrawer-Recommendations");
          if (cartDrawerRecommendations && newRecommendations) {
            cartDrawerRecommendations.innerHTML = newRecommendations.innerHTML;
          } else if (cartDrawerRecommendations && !newRecommendations) {
            // If no more recommendations, remove the section
            cartDrawerRecommendations.remove();
          }

          // Update entire footer (subtotal, badges, payment terms) - all calculated values
          const footer = document.querySelector(".cart-drawer__footer");
          const newFooter = doc.querySelector(".cart-drawer__footer");
          if (footer && newFooter) {
            footer.innerHTML = newFooter.innerHTML;
          }
        }

        // Update cart count
        this.updateCartCount(parsedState.item_count);

        // Check if cart is empty
        const cartDrawer = document.querySelector("cart-drawer");
        if (parsedState.item_count === 0) {
          cartDrawer?.classList.add("is-empty");
        } else {
          cartDrawer?.classList.remove("is-empty");
        }
      }

      getSectionInnerHTML(html, selector) {
        return new DOMParser().parseFromString(html, "text/html").querySelector(selector).innerHTML;
      }

      updateCartCount(count) {
        const cartCountElements = document.querySelectorAll(".cart-count, [data-cart-count]");
        cartCountElements.forEach((element) => {
          element.textContent = count;
          element.classList.toggle("hidden", count === 0);
        });
      }

      formatMoney(cents) {
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
        }).format(cents / 100);
      }
    }
  );
}

// Helper function to format money
function formatMoney(cents) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cents / 100);
}

function getCartDrawerSectionId() {
  return document.getElementById("CartDrawer-Items")?.dataset.id || "cart-drawer";
}

function setCartDrawerBusy(isBusy, message = "Updating cart...") {
  const cartDrawer = document.querySelector("cart-drawer");
  const drawer = document.getElementById("CartDrawer");
  const items = document.getElementById("CartDrawer-Items");
  const checkoutButton = document.querySelector('#CartDrawer-Form button[name="checkout"]');

  cartDrawer?.classList.toggle("is-updating", isBusy);
  drawer?.setAttribute("aria-busy", String(isBusy));

  if (items) {
    items.setAttribute("aria-busy", String(isBusy));
    if (isBusy) items.dataset.cartStatus = message;
    else delete items.dataset.cartStatus;
  }

  if (checkoutButton) checkoutButton.disabled = isBusy;
}

function renderCartDrawerSection(sectionHtml) {
  if (!sectionHtml) return false;

  const cartDrawerItems = document.getElementById("CartDrawer-Items");
  if (!cartDrawerItems) return false;

  const doc = new DOMParser().parseFromString(sectionHtml, "text/html");
  const newItems = doc.querySelector("#CartDrawer-Items");
  if (!newItems) return false;

  const syncOptionalBlock = (selector) => {
    const currentBlock = document.querySelector("cart-drawer")?.querySelector(selector);
    const newBlock = doc.querySelector(selector);

    if (currentBlock && newBlock) {
      currentBlock.replaceWith(newBlock);
    } else if (!currentBlock && newBlock) {
      cartDrawerItems.insertAdjacentElement("beforebegin", newBlock);
    } else if (currentBlock && !newBlock) {
      currentBlock.remove();
    }
  };

  syncOptionalBlock(".cart-drawer__title-row");
  syncOptionalBlock(".cart-drawer__free-shipping-wrap");
  cartDrawerItems.innerHTML = newItems.innerHTML;

  const cartDrawerRecommendations = document.getElementById("CartDrawer-Recommendations");
  const newRecommendations = doc.querySelector("#CartDrawer-Recommendations");
  if (cartDrawerRecommendations && newRecommendations) {
    cartDrawerRecommendations.innerHTML = newRecommendations.innerHTML;
  } else if (cartDrawerRecommendations && !newRecommendations) {
    cartDrawerRecommendations.remove();
  }

  const footer = document.querySelector(".cart-drawer__footer");
  const newFooter = doc.querySelector(".cart-drawer__footer");
  if (footer && newFooter) footer.innerHTML = newFooter.innerHTML;

  return true;
}

function updateCartUi(cart) {
  if (!cart) return;

  document.querySelectorAll(".cart-count, [data-cart-count]").forEach((element) => {
    element.textContent = cart.item_count;
    element.classList.toggle("hidden", cart.item_count === 0);
  });

  const subtotal = document.querySelector("[data-cart-subtotal]");
  if (subtotal) subtotal.textContent = formatMoney(cart.total_price);

  const cartDrawer = document.querySelector("cart-drawer");
  cartDrawer?.classList.toggle("is-empty", cart.item_count === 0);
}

function openCartDrawer() {
  document.querySelector("cart-drawer")?.open();
}

function refreshCartDrawer(cartApi, options = {}) {
  const sectionId = getCartDrawerSectionId();
  const sectionRequest = fetch(`/cart?sections=${encodeURIComponent(sectionId)}`, {
    credentials: "same-origin",
  })
    .then((response) => {
      if (!response.ok) throw new Error("Cart section refresh failed");
      return response.json();
    })
    .then((sections) => renderCartDrawerSection(sections[sectionId]))
    .catch((error) => {
      console.warn(error);
      return false;
    });

  cartApi.invalidate();
  const cartRequest = cartApi
    .fetchCart({ force: true })
    .then((cart) => {
      updateCartUi(cart);
      return cart;
    })
    .catch((error) => {
      console.warn(error);
      return null;
    });

  if (options.open) openCartDrawer();
  return Promise.all([sectionRequest, cartRequest]);
}

function applyCartAddResponse(data, cartApi) {
  const sectionId = getCartDrawerSectionId();
  const sectionRendered = renderCartDrawerSection(data?.sections?.[sectionId]);

  cartApi.invalidate();
  document.querySelector("cart-drawer")?.classList.remove("is-empty");
  openCartDrawer();
  document.dispatchEvent(
    new CustomEvent("cart:item-added", {
      detail: { response: data },
    })
  );

  if (!sectionRendered) {
    return refreshCartDrawer(cartApi).finally(() => setCartDrawerBusy(false));
  }

  setCartDrawerBusy(false);
  cartApi
    .fetchCart({ force: true })
    .then(updateCartUi)
    .catch((error) => console.warn(error));

  return Promise.resolve();
}

async function parseCartResponse(response) {
  const data = await response.json().catch(() => ({}));
  if (response.ok) return data;

  const fallbackMessage = response.status === 429
    ? "Please wait a moment and try again."
    : "Failed to add to cart";
  const error = new Error(data.description || data.message || fallbackMessage);
  error.status = response.status;
  error.isCartRejection = response.status >= 400 && response.status < 500;
  throw error;
}

// Open cart drawer when item is added
document.addEventListener("DOMContentLoaded", () => {
  detectCountryAndToggleFreeShippingBar();

  const cartApi = getCartApi();
  // Handle recommendation button clicks
  document.addEventListener("click", async (e) => {
    const addBtn = e.target.closest("[data-add-to-cart]");
    if (!addBtn) return;

    e.preventDefault();
    if (document.querySelector("cart-drawer")?.classList.contains("is-updating")) return;
    const variantId = Number(addBtn.dataset.variantId);
    const originalText = addBtn.textContent;

    if (!Number.isFinite(variantId)) return;

    setCartDrawerBusy(true, "Adding to cart...");
    openCartDrawer();
    addBtn.classList.add("loading");
    addBtn.disabled = true;
    addBtn.textContent = "Adding...";

    try {
      const response = await fetch("/cart/add.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
          Accept: "application/json",
        },
        credentials: "same-origin",
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: getCartDrawerSectionId(),
          sections_url: window.location.pathname,
        }),
      });
      const data = await parseCartResponse(response);
      await applyCartAddResponse(data, cartApi);
    } catch (error) {
      console.error("Error adding to cart:", error);
      window.CartNotification?.error({ title: "Error!", message: error.message, type: "error" });
    } finally {
      setCartDrawerBusy(false);
      addBtn.classList.remove("loading");
      addBtn.disabled = false;
      addBtn.textContent = originalText;
    }
  });

  // Intercept add to cart forms using event delegation (works for dynamically added forms)
  document.addEventListener(
    "submit",
    (e) => {
      // Get the form element (in case event is from button)
      const form = e.target.closest ? e.target.closest("form") : e.target;
      if (!form || form.tagName !== "FORM") return;

      if (e.submitter?.closest?.(".shopify-payment-button")) return;

      // Check if it's an add to cart form
      const isAddToCartForm = form.action && form.action.includes("/cart/add");

      if (isAddToCartForm) {
        e.preventDefault();
        e.stopPropagation();

        if (
          form.dataset.cartAddProcessing === "true" ||
          document.querySelector("cart-drawer")?.classList.contains("is-updating")
        ) {
          return false;
        }

        form.dataset.cartAddProcessing = "true";

        const body = new FormData(form);
        const variantId = body.get("id");
        const button = form.querySelector('[name="add"], [type="submit"]');
        const originalText = button?.textContent;

        if (!variantId) {
          delete form.dataset.cartAddProcessing;
          window.CartNotification?.error({
            title: "Error!",
            message: "Please select an available option and try again.",
            type: "error",
          });
          return;
        }

        setCartDrawerBusy(true, "Adding to cart...");
        openCartDrawer();
        if (button) {
          button.classList.add("loading");
          button.disabled = true;
          button.textContent = "Adding...";
        }

        body.set("sections", getCartDrawerSectionId());
        body.set("sections_url", window.location.pathname);

        fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "X-Requested-With": "XMLHttpRequest",
            Accept: "application/json",
          },
          credentials: "same-origin",
          body: body,
        })
          .then(parseCartResponse)
          .then((data) => {
            return applyCartAddResponse(data, cartApi);
          })
          .catch((error) => {
            console.error("Error adding to cart:", error);
            if (window.CartNotification) {
              window.CartNotification.error({
                title: "Error!",
                message: error.message,
                type: "error",
              });
            }
          })
          .finally(() => {
            setCartDrawerBusy(false);
            delete form.dataset.cartAddProcessing;
            if (button) {
              button.classList.remove("loading");
              button.disabled = false;
              button.textContent = originalText;
            }
          });

        return false;
      }
    },
    true
  ); // Use capture phase to intercept before other handlers

  // Cart icon click
  document.addEventListener("click", (e) => {
    if (e.target.closest('[href="/cart"]') || e.target.closest("[data-cart-toggle]")) {
      e.preventDefault();
      openCartDrawer();
      refreshCartDrawer(cartApi);
    }
  });

  document.addEventListener("cart:refresh", () => refreshCartDrawer(cartApi));

  // ========== Recommendations Collapse/Expand ==========

  document.addEventListener("click", (e) => {
    const button = e.target.closest("[data-recommendations-toggle]");
    if (!button) return;

    const isExpanded = button.getAttribute("aria-expanded") === "true";
    button.setAttribute("aria-expanded", String(!isExpanded));
  });

  // ========== Shop Pay "View sample plans" bridge ==========
  // Our custom link triggers the official <shopify-payment-terms> CTA living
  // in the hidden host so the official modal opens. Works after AJAX re-renders
  // because the listener is delegated on document.
  document.addEventListener("click", (e) => {
    const link = e.target.closest("[data-shoppay-sample-plans]");
    if (!link) return;
    e.preventDefault();

    const host = document.querySelector(".cart-drawer__payment-terms-host shopify-payment-terms");
    if (!host) return;

    const openOfficialCta = () => {
      const root = host.shadowRoot;
      if (!root) return false;
      const cta = root.getElementById("shopify-installments-cta") || root.querySelector("[data-testid='shopify-installments-cta']");
      if (cta) {
        cta.click();
        return true;
      }
      return false;
    };

    if (openOfficialCta()) return;

    // Shadow root not ready yet (custom-element upgrade pending). Retry briefly.
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      if (openOfficialCta() || attempts >= 10) clearInterval(interval);
    }, 50);
  });
});
